import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscriber } from "rxjs";
import { VERSION } from "src/autogenerated-version";
import { ApiUrls } from "@app/core/shared/data/network/api-urls";
import { ChatMapper } from "@app/core/chat/data/network/chat.mapper";
import { ChatStreamEvent } from "@app/core/chat/domain/model/chat-stream-event";

/**
 * Consumes the streaming chat endpoint (`POST /aiball_chat/chat/stream/`,
 * `text/event-stream`).
 *
 * We use `fetch()` + a streaming reader rather than the shared BaseHandler
 * (HttpClient) because the response is Server-Sent Events: it must be read
 * incrementally and must not be subject to the request timeout. EventSource is
 * not an option either — it cannot POST a body nor set the Authorization
 * header. Unsubscribing aborts the in-flight request.
 *
 * Each SSE frame is `data: <json>\n\n`, where the JSON is one of:
 *   {"type":"delta","text":...}        incremental answer chunk
 *   {"type":"done","response":...,...}  final answer + ids + usage
 *   {"type":"error","error":...}        failure
 *
 * Parsing is deliberately lenient: a successful (2xx) response never surfaces
 * as an error. Heartbeats, empty `data:` lines and non-JSON frames are
 * ignored, so a partially-implemented backend that only returns 200 simply
 * yields no answer instead of a spurious error. Only a non-2xx status, a
 * network failure, or an explicit `error` event reach the error channel.
 */
@Injectable({ providedIn: "root" })
export class SendChatMessageWS {
  constructor(private translateService: TranslateService) {}

  execute(
    question: string,
    token: string,
    conversationId: string | null
  ): Observable<ChatStreamEvent> {
    return new Observable<ChatStreamEvent>((observer) => {
      const controller = new AbortController();

      const run = async (): Promise<void> => {
        const response = await fetch(ApiUrls.CHAT_STREAM_ENDPOINT(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
            "Language-Code": this.languageCode(),
            "App-Version": VERSION,
          },
          body: JSON.stringify(
            ChatMapper.serializeRequest(question, conversationId)
          ),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Chat request failed: ${response.status}`);
        }

        // Some environments expose no readable body on an empty 200; fall back
        // to the buffered text so a valid response never becomes an error.
        if (!response.body) {
          this.emitFrame(await response.text(), observer);
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line.
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";

          for (const frame of frames) {
            this.emitFrame(frame, observer);
          }
        }

        // Flush any trailing bytes / frame left without a blank-line terminator.
        buffer += decoder.decode();
        this.emitFrame(buffer, observer);
      };

      run()
        .then(() => observer.complete())
        .catch((error) => {
          if (!controller.signal.aborted) {
            observer.error(error);
          }
        });

      return () => controller.abort();
    });
  }

  /**
   * Parses a single SSE frame and forwards the resulting event, if any.
   * Frames without a parseable `data:` JSON payload are ignored; an `error`
   * event throws so it reaches the Observable's error channel.
   */
  private emitFrame(
    frame: string,
    observer: Subscriber<ChatStreamEvent>
  ): void {
    const data = frame
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).trim())
      .join("\n")
      .trim();

    if (data.length === 0 || data === "[DONE]") {
      return;
    }

    let payload: any;
    try {
      payload = JSON.parse(data);
    } catch {
      // Half-baked / non-JSON frame — ignore rather than failing the stream.
      return;
    }

    if (payload.type === "delta") {
      observer.next({ type: "delta", text: payload.text ?? "" });
    } else if (payload.type === "done") {
      observer.next({
        type: "done",
        response: ChatMapper.deserialize(payload),
      });
    } else if (payload.type === "error") {
      throw new Error(payload.error ?? "Chat stream error");
    }
  }

  private languageCode(): string {
    return (
      this.translateService.currentLang ||
      this.translateService.getBrowserLang() ||
      ""
    );
  }
}
